/**
 * /settings/organization (v11, admin-only) — aba Geral da Organização fiel ao
 * TiFlux no que temos: seção "Organização" (Nome*, Razão social, CNPJ,
 * Telefone), seção "Sistema" (E-mail da organização, Fuso horário fixo
 * America/Sao_Paulo desabilitado com nota) e "Logo" (preview + upload ≤2MB +
 * remover). Sem "Excluir organização". Salvar único no rodapé (padrão Asaas).
 */
import { useRef, useState, type FormEvent } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ImageIcon, ShieldAlert, Trash2, Upload } from 'lucide-react';
import { ORG_LOGO_MAX_BYTES, type OrgSettings } from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { api, apiUpload, type ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { PageSpinner, Skeleton, Spinner } from '@/components/ui/misc';
// Mesma resolução de mídia do chat: rota autenticada em modo JSON devolve a
// URL presignada, usada direto como src (o hook é genérico sobre o caminho).
import { useChatMedia } from '@/features/chats/use-chat-media';
import { SettingsBreadcrumb } from '@/features/settings/settings-ui';
import { OrgProfileSection } from '@/features/settings/org-profile-section';

const ORG_KEY = ['erp', 'org', 'settings'] as const;
/** spec 017: nome e logo são a MARCA de todas as telas, servida pelo /me */
const ME_KEY = ['erp', 'account', 'profile'] as const;

export function orgSettingsQuery() {
  return queryOptions({
    queryKey: ORG_KEY,
    queryFn: async ({ signal }) => {
      const res = await api<OrgSettings>('/org/settings', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

const ERROR_LABELS: Record<string, string> = {
  validation_error: 'Dados inválidos — confira os campos.',
  payload_too_large: 'A imagem excede o limite de 2MB.',
  unsupported_media_type: 'Formato de imagem não suportado.',
};

function errorMessage(e: unknown): string {
  const err = e as Partial<ApiError> | null;
  const label = err?.error ? ERROR_LABELS[err.error] : undefined;
  if (label) return label;
  if (err?.status === 0) return 'Sem conexão com o servidor.';
  return err?.error ?? 'Erro inesperado.';
}

export function OrganizationSettingsPage() {
  const user = useAuthUser();
  // Admin-style (mesma regra da API): a tela inteira é de administrador.
  if (user?.role !== 'admin') return <RestrictedNotice />;
  return <OrganizationAdmin />;
}

function RestrictedNotice() {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-lg border bg-card text-center shadow-xs">
      <ShieldAlert className="size-8 text-muted-foreground" />
      <p className="text-sm font-medium">Acesso restrito</p>
      <p className="max-w-sm text-sm text-muted-foreground">
        Os dados da organização são configurados por administradores da conta.
      </p>
    </div>
  );
}

function OrganizationAdmin() {
  const settingsQ = useQuery(orgSettingsQuery());

  return (
    <div className="rounded-lg border bg-card shadow-xs">
      <div className="px-4 pt-3 sm:px-5">
        <SettingsBreadcrumb section="Geral" page="Organização" />
        <h1 className="mt-2 text-xl leading-7 font-semibold">
          {settingsQ.data?.name?.trim() || 'Organização'}
        </h1>
        {/* Aba única "Geral" (ink teal), como no TiFlux. */}
        <div className="mt-3 border-b">
          <span className="inline-block border-b-2 border-primary pb-2 text-sm font-medium text-primary">
            Geral
          </span>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        {settingsQ.isPending ? (
          <PageSpinner />
        ) : settingsQ.isError ? (
          <p className="py-10 text-center text-sm text-destructive">
            Erro ao carregar os dados da organização: {errorMessage(settingsQ.error)}
          </p>
        ) : (
          <>
            <OrgForm settings={settingsQ.data} />
            {/* spec 017: vocabulário, recurso, cor e interface padrão do tenant */}
            <div className="mt-8 space-y-4">
              <SectionRule label="Perfil de operação" />
              <OrgProfileSection />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Rótulo de seção sobre a régua horizontal, como no Geral do TiFlux. */
function SectionRule({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3" aria-hidden={false}>
      <span className="h-px w-10 bg-divider" aria-hidden />
      <h2 className="text-[15px] font-medium text-muted-foreground">{label}</h2>
      <span className="h-px flex-1 bg-divider" aria-hidden />
    </div>
  );
}

function OrgForm({ settings }: { settings: OrgSettings }) {
  const qc = useQueryClient();
  const [name, setName] = useState(settings.name ?? '');
  const [legalName, setLegalName] = useState(settings.legalName ?? '');
  const [cnpj, setCnpj] = useState(settings.cnpj ?? '');
  const [phone, setPhone] = useState(settings.phone ?? '');
  const [email, setEmail] = useState(settings.email ?? '');
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api<OrgSettings>('/org/settings', {
        method: 'PUT',
        body: {
          name: name.trim(),
          legalName: legalName.trim() || null,
          cnpj: cnpj.trim() || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
        },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(ORG_KEY, data);
      void qc.invalidateQueries({ queryKey: ME_KEY });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate();
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      {/* ── Organização ──────────────────────────────────────────────────── */}
      <SectionRule label="Organização" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="org-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="org-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="org-legal-name">Razão social</Label>
          <Input
            id="org-legal-name"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="org-cnpj">CNPJ</Label>
          <Input
            id="org-cnpj"
            value={cnpj}
            onChange={(e) => setCnpj(e.target.value)}
            placeholder="00.000.000/0000-00"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="org-phone">Telefone da organização</Label>
          <Input
            id="org-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(00) 00000-0000"
          />
        </div>
      </div>

      {/* ── Sistema ──────────────────────────────────────────────────────── */}
      <SectionRule label="Sistema" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="org-email">E-mail da organização</Label>
          <Input
            id="org-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="org-timezone">Fuso horário</Label>
          <Select id="org-timezone" value="America/Sao_Paulo" disabled onChange={() => undefined}>
            <option value="America/Sao_Paulo">America/Sao_Paulo</option>
          </Select>
          <p className="text-xs text-muted-foreground">
            Todo o produto opera em America/Sao_Paulo — o fuso não é configurável.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
          {save.isPending ? 'Salvando…' : 'Salvar'}
        </Button>
        {saved ? (
          <span className="flex items-center gap-1 text-xs text-[#52C41A]">
            <CheckCircle2 className="size-3.5" />
            Organização salva.
          </span>
        ) : null}
        {save.error ? (
          <span className="text-xs text-destructive" role="alert">
            {errorMessage(save.error)}
          </span>
        ) : null}
      </div>

      {/* ── Logo (upload independente do Salvar — POST próprio) ──────────── */}
      <SectionRule label="Logo" />
      <LogoSection settings={settings} />
    </form>
  );
}

function LogoSection({ settings }: { settings: OrgSettings }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const logo = useChatMedia(settings.logoUrl ?? null);

  const upload = useMutation({
    mutationFn: (file: File) => apiUpload<OrgSettings>('/org/settings/logo', file),
    onSuccess: (data) => {
      qc.setQueryData(ORG_KEY, data);
      // A presignada anterior pode apontar para a logo antiga — invalida.
      void qc.invalidateQueries({ queryKey: ['erp', 'chat-media'] });
      void qc.invalidateQueries({ queryKey: ME_KEY });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  // Remoção: PUT com logoUrl null (contrato v11 — o PUT aceita o campo).
  const remove = useMutation({
    mutationFn: async () => {
      const res = await api<OrgSettings>('/org/settings', {
        method: 'PUT',
        body: { logoUrl: null },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(ORG_KEY, data);
      void qc.invalidateQueries({ queryKey: ME_KEY });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const pick = (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (file.size > ORG_LOGO_MAX_BYTES) {
      setError('A imagem excede o limite de 2MB.');
      return;
    }
    upload.mutate(file);
  };

  const busy = upload.isPending || remove.isPending;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex size-24 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
          {settings.logoUrl ? (
            logo.loading ? (
              <Skeleton className="size-full" aria-busy />
            ) : logo.url ? (
              <img
                src={logo.url}
                alt="Logo da organização"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <ImageIcon className="size-8 text-muted-foreground/50" strokeWidth={1.5} />
            )
          ) : (
            <ImageIcon className="size-8 text-muted-foreground/50" strokeWidth={1.5} />
          )}
        </div>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                pick(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {upload.isPending ? <Spinner className="size-4" /> : <Upload />}
              {settings.logoUrl ? 'Trocar logo' : 'Enviar logo'}
            </Button>
            {settings.logoUrl ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                className="text-destructive hover:text-destructive"
                onClick={() => remove.mutate()}
              >
                <Trash2 />
                Remover
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            Imagem de até 2MB — usada no portal do cliente, nos e-mails e no widget de chat.
          </p>
        </div>
      </div>
      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
